import { CustomerTemplate } from "./types";
import { FieldDataValue }   from "./hooks/customer";

export type ScaleUnit = 'px' | '%w' | '%h';

export interface FontText {
    font: string;
    text: string;
    fillStyle: string | CanvasGradient | CanvasPattern;
}

export interface RenderTextOptions extends FontText {
    x: number;
    y: number;
    maxWidth?: number;
    lineHeight?: number;
    centered?: boolean;
    fromTop?: boolean;
    maxRows?: number;
}

export interface RenderTextResult {

}

type RenderLine = {
    text: string;
    width: number;
}

export interface RenderTextResult {
    lines: number;
    height: number;
    width: number;
}


export interface RenderImageProps {
    image: HTMLImageElement;
    left: number,
    top: number,
    scale?: number
}

export interface RenderSignatureOptions {
    signature: FontText;
    subtitle?: FontText & { offset: number };
    centered?: boolean;
    x: number;
    y: number;
    bottomBoundary?: number;
}

export interface RenderSignatureResult {
    width: number;
    height: number;
}

export class TemplateUtils {
    private readonly imageCache: { [url: string]: Promise<HTMLImageElement> };

    constructor() {
        this.imageCache = {};
    }

    getImage = (url: string): Promise<HTMLImageElement> => {
        if(this.imageCache[url]) {
            return this.imageCache[url];
        }
        console.log("Loading IMG:", url);

        const img = new Image();
        img.src = url;
        const promisedImage =  new Promise(resolve => img.onload = resolve).then(() => {
            return img;
        });

        this.imageCache[url] = promisedImage;
        return promisedImage;
    }


    templateScaler = (template: CustomerTemplate) => (value: number, unit: ScaleUnit = 'px'): number => {
        const { scale, width, height } = template;
        switch (unit) {
            case "px":
                return value * scale;
            case "%w":
                return width * value * scale;
            case "%h":
                return height * value * scale;
            default:
                console.error("Unit", unit, "is not implemented");
                return value;
        }
    }

    fontScaler = (template: CustomerTemplate) => (size: number, font: string, unit: ScaleUnit = 'px') => {
        const s = this.templateScaler(template);

        return `${ s(size, unit) }px ${ font }`;
    }

    getText = (field?: FieldDataValue, defaultValue: string = ''): string => {
        if (field && field.value) {
            return field.value;
        }

        return defaultValue;
    }


    renderText = (ctx: CanvasRenderingContext2D, options: RenderTextOptions): RenderTextResult => {
        const {
            font, text, fillStyle,
            x, y,
            maxRows,
            centered = false,
            maxWidth = -1,
            lineHeight = 0,
            fromTop = true
        } = options;
        ctx.save();
        ctx.fillStyle = fillStyle;
        ctx.font = font;

        let measuredWidth = -1;
        let renderLines: RenderLine[] = [];
        let totalTextHeight = 0;

        if (maxWidth === -1) {
            renderLines = text.split('\n').map(text => {
                const m = ctx.measureText(text);
                measuredWidth = Math.max(m.width, measuredWidth);
                return {
                    text,
                    width: m.width
                }
            });
        } else {
            let lines = text.split('\n');

            for (let i = 0; i < lines.length; i++) {
                const line = lines[i];
                let m = ctx.measureText(line);

                if (m.width <= maxWidth) {
                    renderLines.push({
                        text: line,
                        width: m.width
                    });
                    measuredWidth = Math.max(m.width, measuredWidth);
                    continue;
                }

                const remainder = [];
                const words = line.split(/\s+/g);

                while (words.length > 0) {
                    remainder.push(words.pop());
                    const shortLine = words.join(' ');
                    m = ctx.measureText(shortLine);
                    if (m.width <= maxWidth) {
                        renderLines.push({
                            text: shortLine,
                            width: m.width
                        });
                        measuredWidth = Math.max(m.width, measuredWidth);
                        break;
                    }
                }

                if (remainder.length > 0) {
                    const nId = i + 1;
                    const r = remainder.reverse().join(' ');
                    if (nId >= lines.length) {
                        lines.push(r);
                    } else {
                        lines.splice(nId, 0, r);
                    }
                }
            }
        }

        const maxLines = Math.min(maxRows || 100, renderLines.length);
        for (let i = 0; i < maxLines; i++) {
            const line = renderLines[i];
            const m = ctx.measureText(line.text);
            const textHeight = m.fontBoundingBoxAscent + m.fontBoundingBoxDescent;
            totalTextHeight += textHeight;
            const lO = (lineHeight !== 0 ? lineHeight : textHeight) * (fromTop ? i : i - renderLines.length + 1);
            const left = centered ? x - (line.width / 2) : x;
            ctx.fillText(line.text, left, y + lO);
            measuredWidth = Math.max(m.width, measuredWidth);
        }

        ctx.restore();

        return {
            lines: renderLines.length,
            width: measuredWidth,
            height: lineHeight !== 0 ? lineHeight * maxLines : totalTextHeight
        }
    }

    imageScaler = (template: CustomerTemplate) => (ctx: CanvasRenderingContext2D, options: RenderImageProps): void => {
        const { image, left, top, scale = 1 } = options;
        const { width, height } = image;
        ctx.drawImage(
            image,
            left,
            top,
            (width * template.scale) * scale,
            (height * template.scale) * scale
        );
    }


    renderSignature = (ctx: CanvasRenderingContext2D, options: RenderSignatureOptions): RenderSignatureResult => {
        const { signature, subtitle, centered = false, x, y, bottomBoundary = -1 } = options;

        let width;
        let height;
        let sigOffsetX = 0;
        let subOffsetX = 0;
        let signatureBottom;

        ctx.save();
        ctx.font = signature.font;
        const measureSig = ctx.measureText(signature.text);
        signatureBottom = y + measureSig.fontBoundingBoxAscent + measureSig.fontBoundingBoxDescent;
        ctx.restore();

        if (subtitle && centered) {
            ctx.save();
            ctx.font = subtitle.font;
            const measureSub = ctx.measureText(subtitle.text);
            if (measureSub.width > measureSig.width) {
                sigOffsetX = (measureSub.width - measureSig.width) / 2;
            } else {
                subOffsetX = (measureSig.width - measureSub.width) / 2;
            }
            signatureBottom = Math.max(
                signatureBottom,
                measureSub.fontBoundingBoxAscent + measureSub.fontBoundingBoxDescent + subtitle.offset
            );
            ctx.restore();
        }

        if (bottomBoundary > -1 && signatureBottom > bottomBoundary) {
            return {
                width: 0,
                height: 0
            };
        }

        const signatureSize = this.renderText(ctx, {
            ...signature,
            x: x + sigOffsetX,
            y
        });

        width = signatureSize.width;
        height = signatureSize.height;

        if (subtitle) {
            const subtitleSize = this.renderText(ctx, {
                ...subtitle,
                x: x + subOffsetX,
                y: y + subtitle.offset
            });

            height = Math.max(signatureSize.height, subtitleSize.height + subtitle.offset);
            width = Math.max(width, subtitleSize.width);
        }

        return {
            width,
            height
        }
    }
}
